import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { secureGetPdf, secureDelete, securePost } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import DeletePopup from "../../components/DeletePopup";
import ProductRow from "../../components/ProductRow";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import UserContext from "../../Shared/UserContext";

import trashIcon from "../../icons/trashW.svg";
import exportIcon from "../../icons/export.svg";
import checkIcon from "../../icons/check.svg";

const CreditNoteDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { state: { role } } = useContext(UserContext);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  const creditNote = location.state.creditNote;

  const renderStatus = () => {
    const statusColors = {
      VALIDATED: "bg-[#06D6A0]",
      UNDER_APPROVAL: "bg-[#FEA26F]",
    };

    return (
      <span className={`px-2 py-1 rounded-full font-semibold text-[12px] text-white text-center
        ${statusColors[creditNote.status] || 'bg-gray-500'}`}>
        {t(`br_status.${creditNote.status}`).toUpperCase()}
      </span>
    );
  };

  const handleDeleteClick = () => setDeletePopupVisible(true);

  const handleDeleteConfirm = async () => {
    try {
      await secureDelete(`/creditNotes/delete/${creditNote.id}`, showSnackbar);
      setDeletePopupVisible(false);
      history.push({
        pathname: "/credit-notes",
        state: {
          showSnackbar: true,
          snackbarMessage: t(`confirmation.creditNoteDelete`),
          snackbarSeverity: 'success',
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
        },
      });
    } catch (error) {
      setDeletePopupVisible(false);
      console.error("Error deleting credit note:", error);
    }
  };

  const handleDownloadClick = async () => {
    try {
      const response = await secureGetPdf(`/creditNotes/ba/${creditNote.id}`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Credit_Note_${creditNote.creditNoteNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download credit note:", error);
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      const payload = {
        creditNoteId: creditNote.id,
        status: status,
      };

      await securePost(`creditNotes/updateStatus`, payload, showSnackbar);
      history.push({
        pathname: "/credit-notes",
        state: {
          showSnackbar: true,
          snackbarMessage: status === "VALIDATED" ? t(`confirmation.creditNoteValidation`) : t(`confirmation.creditNoteRejection`),
          snackbarSeverity: 'success'
        }
      });
    } catch (error) {
      console.error("Error rejecting the order:", error);
    }
  }
  const renderActionButtons = () => (
    <>
      {creditNote.status === "UNDER_APPROVAL" && role === "ROLE_ADMIN" && (
        <>
          <button
            className="flex items-center justify-center bg-[#0AD69F] text-[#FFFFFF] font-bold text-[14px] px-6 py-3 rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
            onClick={() => handleUpdateStatus("VALIDATED")}
          >
            <img src={checkIcon} alt="" className="w-5 h-5" />
            {t(`actions.validate`)}
          </button>

        </>
      )}
      {creditNote.status === "VALIDATED" && (

        <button
          className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
          onClick={handleDownloadClick}
        >
          <img src={exportIcon} alt="download" className="w-5 h-5" />
          {t('actions.downloadBA')}
        </button>
      )}
      <button
        className="flex items-center justify-center bg-[#EF476F] text-white px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
        onClick={handleDeleteClick}
      >
        <img src={trashIcon} alt="delete" className="w-5 h-5" />
        {t('actions.delete')}
      </button>
    </>
  );

  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.br_details")}</title>
      </Helmet>

      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-black">
          {t('titles.br_details')}
        </h1>

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-black font-semibold text-[24px]">
              {creditNote.creditNoteNumber}
            </p>
            <div className="flex">{renderStatus()}</div>
          </div>
          <div className="flex space-x-4">{renderActionButtons()}</div>
        </div>

        <div>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {creditNote.client?.code} - {creditNote.client?.name}
          </p>
        </div>
      </div>

      <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.creationDate')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(creditNote.createdAt)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.commercial')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {creditNote.user?.firstName} {creditNote.user?.lastName}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            TOTAL HT
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(creditNote.priceHT)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            TOTAL TTC
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(creditNote.priceTTC)}
          </p>
        </div>
      </div>

      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t('fields_types.list')}
      </h3>

      <div className="space-y-2">
        {creditNote.details?.map((product, index) => (
          <ProductRow key={index} product={product} />
        ))}
      </div>

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default CreditNoteDetails;